import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './CuatomerReceipt.css';

const CuatomerReceipt = forwardRef(
    (
        {
            order,
            orders = [],
            brandName = '',
            taxPercent = 0,
            logo = '',
            phoneNumber = '',
            addres = '',
        },
        ref
    ) => {
        const { t, i18n } = useTranslation();
        const receiptRef = useRef(null);

        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';

        const formatNumber = (num) => new Intl.NumberFormat(locale).format(Number(num ?? 0));

        const formatReceiptTime = (dateString) => {
            if (!dateString) return '-';

            return new Date(dateString).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        };

        const formatReceiptDate = (dateString) => {
            if (!dateString) return '-';

            return new Date(dateString).toLocaleDateString(locale);
        };

        const getTableDisplayName = (currentOrder) => {
            const tableId = currentOrder?.table_id ?? currentOrder?.table;

            if (tableId === null || tableId === undefined || tableId === '') {
                return t('take_away_order') || 'بیرون‌بر';
            }

            return tableId;
        };
        const toEnglishDigits = (value) =>
            String(value ?? '')
                .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
                .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));
        const toPersianDigits = (value) =>
            String(value ?? '').replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[digit]);
        const formatPhoneNumber = (phone) => {
            if (!phone) {
                return '-';
            }
            let normalizedPhone = toEnglishDigits(phone);
            normalizedPhone = normalizedPhone.replace(/\D/g, '');
            if (normalizedPhone.startsWith('98')) {
                normalizedPhone = `0${normalizedPhone.slice(2)}`;
            }

            if (normalizedPhone.length === 10 && normalizedPhone.startsWith('9')) {
                normalizedPhone = `0${normalizedPhone}`;
            }
            if (i18n.language === 'fa') {
                return toPersianDigits(normalizedPhone);
            }
            return normalizedPhone;
        };
        const getCustomerName = (currentOrder) =>
            currentOrder?.customer_name || t('anonymous_customer') || 'مشتری ناشناس';

        const getCustomerId = (currentOrder) => currentOrder?.customer ?? 0;

        const getCustomerPhone = (currentOrder) => {
            if (!currentOrder?.customer_phone_number) {
                return '-';
            }

            return currentOrder.customer_phone_number;
        };

        const getOrderTotal = (currentOrder) => {
            if (!Array.isArray(currentOrder?.items)) {
                return 0;
            }

            return currentOrder.items.reduce((sum, item) => sum + Number(item.total_price ?? 0), 0);
        };

        const getOrderDiscount = (currentOrder) => Number(currentOrder?.discount ?? 0);

        const getTaxableAmount = (currentOrder) => {
            const subtotal = getOrderTotal(currentOrder);
            const discount = getOrderDiscount(currentOrder);

            return Math.max(subtotal - discount, 0);
        };

        const getTaxAmount = (currentOrder) => {
            const taxableAmount = getTaxableAmount(currentOrder);

            return (taxableAmount * Number(taxPercent ?? 0)) / 100;
        };

        const getOrderTotalPrice = (currentOrder) => {
            const taxableAmount = getTaxableAmount(currentOrder);
            const taxAmount = getTaxAmount(currentOrder);

            return taxableAmount + taxAmount;
        };

        const getCashierName = () => {
            try {
                const storedUser = localStorage.getItem('fullName');

                if (storedUser) {
                    const parsedUser = JSON.parse(storedUser);

                    return parsedUser?.fullName || parsedUser?.full_name || parsedUser?.name || '';
                }
            } catch (error) {
                console.error('Could not read cashier information.', error);
            }

            return '';
        };

        const getDailyOrderNumber = (currentOrder) => {
            if (!currentOrder || !orders.length) {
                return '-';
            }

            const orderDate = new Date(currentOrder.created_at).toDateString();

            const sameDayOrders = orders
                .filter((item) => new Date(item.created_at).toDateString() === orderDate)
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

            const index = sameDayOrders.findIndex((item) => item.id === currentOrder.id);

            return index !== -1 ? index + 1 : '-';
        };
        const handlePrint = () => {
            if (!receiptRef.current || !order) {
                return;
            }

            const printContent = receiptRef.current.outerHTML;

            const printWindow = window.open('', '_blank', 'width=400,height=700,scrollbars=yes');
            const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
                .map((element) => element.outerHTML)
                .join('\n');
            if (!printWindow) {
                console.error('Could not open print window.');
                return;
            }

            printWindow.document.open();
            printWindow.document.write(`
                <!DOCTYPE html>
                <html lang="${i18n.language === 'fa' ? 'fa' : 'en'}">
                    <head>
                        <meta charset="UTF-8" />
                        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            
                        <title>Order ${order.order_id || order.id || 'receipt'}</title>
            
                        ${styles}
                    </head>

                    <body>
                        ${printContent}

                        <script>
                            window.addEventListener('load', async () => {
                                try {
                                    if (document.fonts && document.fonts.ready) {
                                        await document.fonts.ready;
                                    }
                                } catch (e) {}

                                const images = Array.from(document.images);

                                await Promise.all(
                                    images.map((img) => {
                                        if (img.complete) {
                                            return Promise.resolve();
                                        }
                                        return new Promise((resolve) => {
                                            img.onload = resolve;
                                            img.onerror = resolve;
                                        });
                                    })
                                );
                                setTimeout(() => {
                                    window.focus();
                                    window.print();
                                    setTimeout(() => {
                                        window.close();
                                    }, 300);
                                }, 300);
                            });
                        </script>
                    </body>
                </html>
            `);
            printWindow.document.close();
        };
        useImperativeHandle(ref, () => ({
            print: handlePrint,
        }));

        if (!order) {
            return <p>{t('loading')}</p>;
        }

        const cashierName = getCashierName();

        return (
            <div ref={receiptRef} className="customer_receipt">
                <div className="customer_receipt_header">
                    <div className="customer_receipt_header_side">
                        <div className="customer_receipt_header_box">
                            <span className="customer_receipt_box_label">
                                {t('order_id') || 'شماره فاکتور'}
                            </span>

                            <strong>{formatNumber(getDailyOrderNumber(order))}</strong>
                        </div>
                        <span className="customer_receipt_date_and_time">
                            {formatReceiptTime(order.created_at)}
                        </span>
                    </div>
                    <div className="customer_receipt_brand">
                        <img
                            src={logo}
                            alt={brandName || 'Cafe'}
                            className="customer_receipt_logo"
                        />
                        <div className="customer_receipt_brand_name">{brandName}</div>
                    </div>
                    <div className="customer_receipt_header_side">
                        <div className="customer_receipt_header_box">
                            <span className="customer_receipt_box_label">
                                {t('customer_id') || 'شناسه مشتری'}
                            </span>

                            <strong>{formatNumber(getCustomerId(order))}</strong>
                        </div>

                        <span className="customer_receipt_date_and_time">
                            {formatReceiptDate(order.created_at)}
                        </span>
                    </div>
                </div>

                <div className="customer_receipt_separator" />
                <div className="customer_receipt_customer">
                    <div className="customer_receipt_customer_row">
                        <span>{t('customer') || 'مشتری'}:</span>

                        <strong>{getCustomerName(order)}</strong>
                    </div>

                    <div className="customer_receipt_customer_row">
                        <span>{t('waiter') || 'نام گارسون'}:</span>

                        <strong>&nbsp;</strong>
                    </div>

                    <div className="customer_receipt_customer_row">
                        <span>{t('table_number') || 'میز'}:</span>

                        <strong>{getTableDisplayName(order)}</strong>
                    </div>
                </div>

                <div className="customer_receipt_separator" />
                <div className="customer_receipt_items">
                    <table>
                        <thead className="customer_receipt_items_table_header">
                            <tr>
                                <th className="customer_receipt_item_name">
                                    {t('item') || 'کالا'}
                                </th>

                                <th className="customer_receipt_item_price">
                                    {t('price') || 'قیمت'}
                                </th>

                                <th className="customer_receipt_item_quantity">
                                    {t('quantity') || 'تعداد'}
                                </th>

                                <th className="customer_receipt_item_total">
                                    {t('total') || 'مبلغ'}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {Array.isArray(order.items) &&
                                order.items.map((item) => (
                                    <tr key={item.id}>
                                        <td className="customer_receipt_item_name">
                                            {item.item_title || item.item?.name || '-'}
                                        </td>

                                        <td className="customer_receipt_item_price">
                                            {formatNumber(item.item_price)}
                                        </td>

                                        <td className="customer_receipt_item_quantity">
                                            {formatNumber(item.quantity)}
                                        </td>

                                        <td className="customer_receipt_item_total">
                                            {formatNumber(item.total_price)}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                <div className="customer_receipt_separator" />
                <div className="customer_receipt_totals">
                    <div className="customer_receipt_total_row">
                        <span>{t('total_price') || 'جمع کل'}:</span>

                        <strong>{formatNumber(getOrderTotal(order))}</strong>
                    </div>

                    <div className="customer_receipt_total_row">
                        <span>{t('discount') || 'تخفیف'}:</span>

                        <strong>{formatNumber(getOrderDiscount(order))}</strong>
                    </div>

                    <div className="customer_receipt_total_row">
                        <span>
                            {t('tax') || 'مالیات'} ({taxPercent}%):
                        </span>

                        <strong>{formatNumber(getTaxAmount(order))}</strong>
                    </div>

                    <div className="customer_receipt_total_row customer_receipt_final_total">
                        <span>{t('final_total') || 'مبلغ قابل پرداخت'}:</span>

                        <strong>
                            {formatNumber(getOrderTotalPrice(order))} {t('toman') || 'تومان'}
                        </strong>
                    </div>
                </div>

                <div className="customer_receipt_separator" />
                <div className="customer_receipt_bottom">
                    <div className="customer_receipt_bottom_row">
                        <span>{t('cashier') || 'صندوقدار'}:</span>

                        <strong>{localStorage.getItem('fullName')}</strong>
                    </div>

                    <div className="customer_receipt_bottom_row">
                        <span>{t('call_number') || 'شماره تماس'}:</span>

                        <strong>{formatPhoneNumber(phoneNumber)}</strong>
                    </div>
                    <div className="customer_receipt_bottom_row_address">
                        <span>{t('address')}:</span>

                        <strong>{addres}</strong>
                    </div>
                </div>

                <div className="customer_receipt_separator" />
                <div className="customer_receipt_bottom_row_brand_name">
                    توسعه داده شده توسط تیم ملاین me-line.ir
                </div>
                <div className="customer_receipt_quote" />
            </div>
        );
    }
);

CuatomerReceipt.displayName = 'CuatomerReceipt';

export default CuatomerReceipt;
